import { formatDate } from '@angular/common';
import { Component, Inject, Input, LOCALE_ID, OnInit } from '@angular/core';
import { NbDialogRef, NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { AlTableBadgeComponent } from '../../features/table-badge/table-badge.component';
import {
  AlTableColumn,
  AlTableRowAction,
} from '../../features/table/table.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import {
  GuardrailHit,
  GuardrailRule,
  GuardrailService,
} from '../../../services/guardrail.service';

/**
 * Trigger history of one guardrail rule.
 *
 * A modal rather than an inline expansion: per `design.md` "Row Action Icons"
 * the eye action opens the deep detail view as a dialog. It also keeps the
 * rule table a plain `app-table`, which has no row-expansion concept.
 */
@Component({
  selector: 'app-guardrail-hits-dialog',
  templateUrl: './guardrail-hits-dialog.component.html',
  styleUrl: './guardrail-hits-dialog.component.scss',
})
export class GuardrailHitsDialogComponent implements OnInit {
  @Input() rule!: GuardrailRule;

  hits: GuardrailHit[] = [];
  total = 0;
  isLoading = false;

  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];

  constructor(
    protected dialogRef: NbDialogRef<GuardrailHitsDialogComponent>,
    private guardrailService: GuardrailService,
    private dialogService: NbDialogService,
    private toastrService: NbToastrService,
    private t: TranslateService,
    @Inject(LOCALE_ID) private locale: string
  ) {}

  ngOnInit(): void {
    this.columns = [
      {
        key: 'created_at', kind: 'date',
        title: this.t.instant('pages.guardrails.fields.hitAt'),
        width: '150px',
        sortable: true,
        sortDefault: 'desc',
        map: (value: string) =>
          value ? formatDate(value, 'dd.MM.yy HH:mm', this.locale) : '—',
      },
      {
        key: 'action_taken',
        title: this.t.instant('pages.guardrails.fields.action'),
        width: '150px',
        component: AlTableBadgeComponent,
        componentInputs: ({ value }) => ({
          label: value
            ? this.t.instant('pages.guardrails.action.' + value)
            : '—',
          variant:
            value === 'block'
              ? 'danger'
              : value === 'redirect'
                ? 'warning'
                : 'muted',
        }),
      },
      {
        key: 'confidence', kind: 'number',
        title: this.t.instant('pages.guardrails.fields.confidence'),
        width: '110px',
        map: (value: number | null) =>
          value === null || value === undefined ? '—' : value.toFixed(2),
      },
      {
        key: 'matched_text', kind: 'text',
        title: this.t.instant('pages.guardrails.fields.matchedText'),
        map: (value: string) => value || '—',
      },
    ];

    this.rowActions = [
      {
        key: 'delete',
        title: this.t.instant('buttons.delete'),
        icon: 'trash-outline',
        isMultiAction: true,
      },
    ];

    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.guardrailService.listHits(this.rule.id).subscribe({
      next: (result) => {
        this.hits = result.data;
        this.total = result.total;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  /** True when the fetch limit cut the history short — surfaced, never silent. */
  get truncated(): boolean {
    return this.total > this.hits.length;
  }

  // ── Deleting entries ──
  //
  // Id-based, never "clear the whole rule": the operator prunes noise while
  // tuning a rule, and wiping the history wholesale would destroy the very
  // evidence the log exists for. Same in both scopes — the dialog does not
  // care whether the rule is organisation-wide or agent-specific.

  onTableEvent(payload: { event: string; data: any }): void {
    const rows: GuardrailHit[] =
      payload.event === 'multiAction'
        ? payload.data?.rows || []
        : payload.event === 'rowAction' && payload.data?.row
          ? [payload.data.row]
          : [];
    if (!rows.length) return;
    if (
      payload.data?.action?.key !== 'delete'
    ) {
      return;
    }
    this.confirmDelete(rows);
  }

  private confirmDelete(rows: GuardrailHit[]): void {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.guardrails.hitDeleteTitle',
        message: this.t.instant('pages.guardrails.hitDeleteMessage', {
          count: rows.length,
        }),
        onConfirm: () => {
          this.isLoading = true;
          this.guardrailService
            .deleteHits(
              this.rule.id,
              rows.map((r) => r.id)
            )
            .subscribe({
              next: () => {
                this.toastrService.success(
                  this.t.instant('pages.guardrails.hitsDeleted', {
                    count: rows.length,
                  }),
                  ''
                );
                ref.close();
                this.load();
              },
              error: () => {
                this.isLoading = false;
                this.toastrService.danger(
                  this.t.instant('pages.guardrails.hitDeleteFailed'),
                  ''
                );
              },
            });
        },
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
